import React, { useCallback, useContext, useRef } from "react";
import clsx from "clsx";

import Text, { BaseTextProps } from "../../common/Text/Text";
import * as NavigationUtils from "../../../lib/util/NavigationUtils";
import * as AnalyticsUtils from "../../../lib/util/AnalyticsUtils";
import { ElementType, ExtractMixinProps } from "../../../lib/types/UtilTypes";
import { usePreload } from "../../../lib/hook/NavigationHooks";

export interface BaseLinkProps<C extends ElementType = "span">
	extends BaseTextProps<C> {
	href?: string;
	includeFrom?: boolean;
	ignoreChildClicks?: boolean;
	sameWindow?: boolean;
}

export type LinkProps<C extends ElementType = "span"> = ExtractMixinProps<
	BaseLinkProps<C>
>;

function Link<C extends ElementType = "span">(props: LinkProps<C>) {
	const {
		children,
		href,
		className,
		disabled,
		onClick,
		noChild,
		ignoreChildClicks,
		includeFrom,
		doNotTrack,
		sameWindow,
		...otherProps
	} = props;

	const trackingContext = useContext(AnalyticsUtils.TrackingContext);
	const ref = useRef<HTMLElement | null>(null);

	const handleClick = useCallback(
		(e) => {
			if (onClick) {
				onClick(e);
			}

			if (e.isDefaultPrevented() || disabled || !href) {
				return e.preventDefault();
			}
			if (ignoreChildClicks) {
				if (ref.current !== e.target) {
					return e.preventDefault();
				}
			}

			NavigationUtils.link(href, e, { includeFrom, sameWindow });

			if (doNotTrack) {
				return;
			}

			const evt = {
				eventCategory: "Link",
				eventAction: "click",
				eventLabel: href,
				mixpanelProperties: {
					name: "Link Click",
					Context:
						typeof trackingContext === "string" ? trackingContext : undefined,
					"Link URL": href,
				},
			};

			if (typeof trackingContext !== "string" && trackingContext.mixpanel) {
				evt.mixpanelProperties = {
					...evt.mixpanelProperties,
					...trackingContext.mixpanel,
				};
			}

			AnalyticsUtils.sendEvent(evt);
		},
		[
			onClick,
			disabled,
			href,
			ignoreChildClicks,
			includeFrom,
			doNotTrack,
			trackingContext,
			sameWindow,
		],
	);

	const role = href ? "a" : "span";
	usePreload(href);

	return (
		<Text
			forwardRef={ref}
			tag={role}
			className={clsx("Link", disabled && "--disabled", className)}
			href={href}
			onClick={handleClick}
			{...otherProps}
		>
			{noChild ? null : children || href}
		</Text>
	);
}

export default React.memo(Link);
